/**
 * The sign-in screen.
 *
 * It is the first thing anyone sees, and it is doing two jobs at once: letting
 * people in, and telling them what kind of tool this is. A group deciding
 * whether to trust a workbench with what they really think about their employer
 * will read this page for evidence, so it states the two facts that matter —
 * no school systems are involved, and private marks stay private — rather than
 * a welcome message.
 *
 * It never says whether an address is known. Seven people are bargaining
 * against their employer, and an endpoint that distinguishes "no such address"
 * from "link sent" enumerates the group.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api.js';

/**
 * Tokens already sent for exchange, across mounts.
 *
 * The link is single-use, and React StrictMode double-mounts in development.
 * This Set is the one and only guard: mount 2 skips the effect, and mount 1's
 * request resolves normally. (An earlier `cancelled` cleanup flag alongside
 * it suppressed that resolution — the only request ever sent — hanging every
 * dev sign-in on "Signing you in…". A post-unmount setState is a no-op in
 * React 18, so no cleanup is needed at all.) Module scope survives the
 * remount; a page reload starts clean, which is right — by then the token is
 * spent either way.
 */
const exchanged = new Set<string>();

type Stage = 'entry' | 'sent' | 'exchanging' | 'failed';

export function SignIn({
  linkToken,
  onSignedIn,
}: {
  /** A token lifted from `#/sign-in?token=…`, if the visitor arrived by link. */
  linkToken: string | null;
  onSignedIn: () => void;
}) {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<Stage>(linkToken ? 'exchanging' : 'entry');
  const [busy, setBusy] = useState(false);

  // Arriving with a token: exchange it once, then hand back to the app.
  useEffect(() => {
    if (!linkToken || exchanged.has(linkToken)) return;
    exchanged.add(linkToken);

    void api
      .exchange(linkToken)
      .then(() => {
        // Drop the token from the address bar before anything else happens,
        // so it does not sit in history or in a screenshot of the browser.
        window.history.replaceState(null, '', '#/read');
        onSignedIn();
      })
      .catch(() => setStage('failed'));
    // Deliberately keyed on the token alone: onSignedIn changing must not
    // re-run an exchange that can only happen once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.requestLink(email);
      setStage('sent');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-7">
      <p className="label">Tyndale teaching staff</p>
      <h1 className="register-reading mt-1" style={{ fontSize: 22 }}>
        Teachers Lounge
      </h1>

      {stage === 'sent' ? (
        <Sent email={email} onAgain={() => setStage('entry')} />
      ) : stage === 'exchanging' ? (
        <p className="register-reading mt-6" style={{ fontSize: 15 }}>
          Signing you in…
        </p>
      ) : (
        <form onSubmit={submit} className="mt-6">
          {stage === 'failed' ? (
            <p
              className="mb-4 border-l-2 pl-3 text-[13px]"
              style={{ borderColor: 'var(--color-ink)', color: 'var(--color-muted)' }}
            >
              That link is no longer valid — links work once, and expire after
              fifteen minutes. Ask for a new one below.
            </p>
          ) : null}

          <label htmlFor="email" className="label">
            Your email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full border-b bg-transparent py-2 outline-none"
            style={{ borderColor: 'var(--color-rule)', fontSize: 15 }}
          />

          <button
            type="submit"
            disabled={busy}
            className="mt-5 border px-4 py-2 text-[13px] disabled:opacity-50"
            style={{ borderColor: 'var(--color-ink)' }}
          >
            {busy ? 'Sending…' : 'Send me a sign-in link'}
          </button>

          <div
            className="mt-8 border-t pt-4 text-[12px] leading-relaxed"
            style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}
          >
            <p>
              No password, and nothing here runs through school systems — not
              sign-in, not email, not storage.
            </p>
            <p className="mt-2">
              Marks you make are private until you publish them. Only invited
              representatives can sign in.
            </p>
          </div>
        </form>
      )}
    </main>
  );
}

function Sent({ email, onAgain }: { email: string; onAgain: () => void }) {
  return (
    <div className="mt-6">
      <p className="register-reading" style={{ fontSize: 15 }}>
        If {email} belongs to a representative, a sign-in link is on its way. It
        works once, and expires in fifteen minutes.
      </p>
      <button
        onClick={onAgain}
        className="mt-5 text-[12px] underline"
        style={{ color: 'var(--color-muted)' }}
      >
        Use a different address
      </button>
    </div>
  );
}
